/**
 * Carrier / telco offer filter (2026-06-25).
 *
 * Mirrors the server-side `_CARRIER_DOMAINS` in
 * api/app/services/search/acceptance_contract.py. AU mobile carriers
 * retail handsets on plans, so their phone "price" is a plan/contract
 * figure, NOT an outright single-purchase price. They must never appear
 * as an outright comparable or be cited as price-match proof.
 *
 * The server acceptance contract already drops carriers from NEW
 * searches; this drops them from the rows the web renders so a STALE
 * cached comparison doesn't surface a carrier plan price as the cheapest
 * "best" - which then dead-ends the Negotiate flow at the server carrier
 * backstop with a 400 ("competitor_not_outright_priced").
 *
 * KEEP THIS LIST IN SYNC with the server set.
 */

const CARRIER_DOMAINS: readonly string[] = [
  "telstra.com.au",
  "optus.com.au",
  "vodafone.com.au",
  "tpg.com.au",
  "boost.com.au",
  "boostmobile.com.au",
  "belong.com.au",
  "felixmobile.com.au",
];

function hostOf(url: string): string {
  try {
    return new URL(url).host.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

/** True when the offer's URL is on (a subdomain of) a known AU carrier. */
export function isCarrierOffer(product: {
  product_url?: string | null;
}): boolean {
  const host = hostOf(product?.product_url || "");
  if (!host) return false;
  return CARRIER_DOMAINS.some((c) => host === c || host.endsWith("." + c));
}

/** Drop carrier offers from a flat product list. */
export function dropCarrierOffers<T extends { product_url?: string | null }>(
  products: T[],
): T[] {
  return products.filter((p) => !isCarrierOffer(p));
}
